/**
 * FlowNode Component
 *
 * Expandable box representing a single contribution or data source in the flow diagram.
 * Shows input/output data when expanded.
 */

import { clsx } from 'clsx';
import { ChevronDown, ChevronRight, Clock, Coins, Hash } from 'lucide-react';
import { ExpertBadge, ModeratorBadge } from './ExpertBadge';
import { JsonDataViewer } from './JsonDataViewer';
import type { LLMProvider, ContributionType } from '../../../lib/api';

export type FlowNodeType = 'input' | 'proposal' | 'critique' | 'defense' | 'synthesis' | 'data';

interface FlowNodeProps {
  // Identity
  nodeId: string;
  nodeType: FlowNodeType;

  // Appearance
  title: string;
  subtitle?: string;
  model?: LLMProvider;
  isModeratorAgent?: boolean;
  contributionType?: ContributionType;

  // Data
  inputData?: unknown;
  outputData?: unknown;
  /** The full prompt sent to the model */
  prompt?: string | null;

  // Metrics
  confidence?: number | null;
  latencyMs?: number | null;
  costUsd?: number | null;
  tokenUsage?: { input: number; output: number } | null;
  positionChanged?: boolean;

  // Expansion
  isExpanded: boolean;
  onToggle: () => void;
}

const NODE_TYPE_CONFIG: Record<FlowNodeType, { icon: string; label: string }> = {
  input: { icon: '📥', label: 'Input Data' },
  data: { icon: '📊', label: 'Data' },
  proposal: { icon: '📝', label: 'Proposal' },
  critique: { icon: '🔍', label: 'Critique' },
  defense: { icon: '🛡️', label: 'Defense' },
  synthesis: { icon: '⚖️', label: 'Synthesis' },
};

const MODEL_COLORS: Record<LLMProvider | 'moderator' | 'input', { bg: string; border: string; text: string }> = {
  CLAUDE_OPUS_4_5: {
    bg: 'bg-purple-50',
    border: 'border-purple-300',
    text: 'text-purple-700',
  },
  CLAUDE_OPUS_4_6: {
    bg: 'bg-violet-50',
    border: 'border-violet-300',
    text: 'text-violet-700',
  },
  GPT_5_2_PRO: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-300',
    text: 'text-emerald-700',
  },
  GEMINI_3_PRO: {
    bg: 'bg-blue-50',
    border: 'border-blue-300',
    text: 'text-blue-700',
  },
  moderator: {
    bg: 'bg-indigo-50',
    border: 'border-indigo-300',
    text: 'text-indigo-700',
  },
  input: {
    bg: 'bg-gray-50',
    border: 'border-gray-300',
    text: 'text-gray-700',
  },
};

export function FlowNode({
  nodeId,
  nodeType,
  title,
  subtitle,
  model,
  isModeratorAgent,
  contributionType: _contributionType, // May be used in future for filtering
  inputData,
  outputData,
  prompt,
  confidence,
  latencyMs,
  costUsd,
  tokenUsage,
  positionChanged,
  isExpanded,
  onToggle,
}: FlowNodeProps) {
  void _contributionType; // Silence unused variable warning
  const typeConfig = NODE_TYPE_CONFIG[nodeType] || NODE_TYPE_CONFIG.data;

  // Determine colors based on model or node type
  const colorKey = isModeratorAgent
    ? 'moderator'
    : model
      ? model
      : 'input';
  const colors = MODEL_COLORS[colorKey] || MODEL_COLORS.input;

  return (
    <div
      id={nodeId}
      className={clsx(
        'rounded-lg border-2 overflow-hidden transition-all',
        colors.bg,
        colors.border,
        isExpanded ? 'shadow-md' : 'shadow-sm'
      )}
    >
      {/* Header */}
      <div
        className={clsx(
          'flex items-center justify-between p-3 cursor-pointer',
          'hover:bg-white/30 transition-colors'
        )}
        onClick={onToggle}
      >
        <div className="flex items-center gap-2 min-w-0">
          {isExpanded ? (
            <ChevronDown className={clsx('h-4 w-4 flex-shrink-0', colors.text)} />
          ) : (
            <ChevronRight className={clsx('h-4 w-4 flex-shrink-0', colors.text)} />
          )}

          <span className="text-lg flex-shrink-0">{typeConfig.icon}</span>

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={clsx('font-medium text-sm', colors.text)}>{title}</span>
              {positionChanged && (
                <span className="px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-700 border border-amber-200">
                  Position Changed
                </span>
              )}
            </div>
            {subtitle && (
              <p className="text-xs text-gray-500 truncate">{subtitle}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Model Badge */}
          {model && !isModeratorAgent && (
            <ExpertBadge model={model} size="sm" showIcon={false} />
          )}
          {isModeratorAgent && <ModeratorBadge size="sm" />}

          {/* Confidence */}
          {confidence != null && (
            <span className="text-xs font-medium text-gray-600 bg-white/50 px-2 py-0.5 rounded">
              {confidence}%
            </span>
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-3 space-y-3 bg-white/50">
          {/* Metrics Row */}
          {(latencyMs || costUsd || tokenUsage) && (
            <div className="flex items-center gap-4 text-xs text-gray-500 pb-2 border-b border-gray-100">
              {latencyMs && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {(latencyMs / 1000).toFixed(1)}s
                </span>
              )}
              {costUsd != null && (
                <span className="flex items-center gap-1">
                  <Coins className="h-3 w-3" />
                  ${costUsd.toFixed(4)}
                </span>
              )}
              {tokenUsage && (
                <span className="flex items-center gap-1">
                  <Hash className="h-3 w-3" />
                  {tokenUsage.input.toLocaleString()} in / {tokenUsage.output.toLocaleString()} out
                </span>
              )}
            </div>
          )}

          {/* Prompt (from Langfuse) */}
          {prompt && (
            <JsonDataViewer
              data={prompt}
              title="Prompt Sent to Model"
              defaultExpanded={false}
              maxHeight={400}
            />
          )}

          {/* Input Data */}
          {inputData !== undefined && (
            <JsonDataViewer
              data={inputData}
              title="Input Data"
              defaultExpanded={false}
              maxHeight={300}
            />
          )}

          {/* Output Data */}
          {outputData !== undefined && (
            <JsonDataViewer
              data={outputData}
              title="Output / Content"
              defaultExpanded={true}
              maxHeight={400}
            />
          )}

          {/* Show message if no data */}
          {inputData === undefined && outputData === undefined && !prompt && (
            <p className="text-sm text-gray-400 italic text-center py-4">
              No data available
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Simple node for input data sections
 */
export function InputDataNode({
  title,
  data,
  icon: _icon = '📊', // Reserved for future use
  isExpanded,
  onToggle,
}: {
  title: string;
  data: unknown;
  icon?: string;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  void _icon; // Silence unused variable warning
  return (
    <FlowNode
      nodeId={`input-${title.toLowerCase().replace(/\s+/g, '-')}`}
      nodeType="input"
      title={title}
      subtitle={
        typeof data === 'object' && data
          ? `${Object.keys(data).length} fields`
          : typeof data
      }
      outputData={data}
      isExpanded={isExpanded}
      onToggle={onToggle}
    />
  );
}
